<script setup lang="ts">
import type { NavLink } from './Item.vue'
import { SECTIONS, resultsPath, type SectionKey } from '~/composables/results'
import { groupTypeMeta } from '~/composables/groupManager'

const route = useRoute()
const openFeature = useOpenFeature()
// Results links follow the year being browsed, so the menu stays open on it.
const currentYear = useFestivalYear()
const festivalYear = computed(() => {
  const browsing = route.path.match(/^\/admin\/results\/(\d{4})(\/|$)/)
  return browsing ? Number(browsing[1]) : currentYear.value
})
const { studios, loaded } = storeToRefs(useFestivalManager())
const { festivalSubmissions } = storeToRefs(useFiveFestSubmission())
const { user, roles, impersonating } = storeToRefs(useSession())
const { impersonate } = useSession()

/** Open state on phones and small tablets, where the drawer floats over the page. */
const open = defineModel<boolean>({ required: true })
const { smAndDown: mobile } = useDisplay()

/** Icons-only on desktop, remembered between visits. */
const collapsed = useLocalStorage('sato48:nav:rail', false)
const rail = computed(() => collapsed.value && !mobile.value)
const toggleRail = () => (collapsed.value = !collapsed.value)

onKeyStroke('[', e => {
  const el = e.target as HTMLElement | null
  if (mobile.value || e.metaKey || e.ctrlKey || e.altKey) return
  if (el?.closest('input, textarea, select, [contenteditable="true"]')) return
  toggleRail()
})

const live = computed(() => openFeature.getObject('live'))

/** The pre-Results page for each section, under /admin/results. */
const LEGACY_RESULTS: Record<SectionKey, string> = {
  selection: 'selection',
  jury: 'judge',
  academy: 'academy',
}

// Router links only count as active when the page is a child route of the
// link's own route, which misses most pages here (archive years are siblings
// of /archive). Match on the path instead, and let only the most specific
// link win, so Get Started doesn't light up under every Film Challenge page.
const path = computed(() => route.path.replace(/(.)\/+$/, '$1'))
const matches = (to?: string) =>
  !!to && (to === '/' ? path.value === '/' : path.value === to || path.value.startsWith(`${to}/`))
const targets = (items: NavLink[]): string[] =>
  items.flatMap(item => [...(item.to ? [item.to] : []), ...targets(item.children ?? [])])
const markActive = (items: NavLink[]): NavLink[] => {
  const best = targets(items)
    .filter(matches)
    .sort((a, b) => b.length - a.length)[0]
  const mark = (list: NavLink[]): NavLink[] =>
    list.map(item => ({
      ...item,
      active: item.to ? (item.active ?? item.to === best) : item.active,
      children: item.children && mark(item.children),
    }))
  return mark(items)
}

const links = computed(() => {
  const l: NavLink[] = []
  /** A headed group of links, left out entirely when nothing in it is on. */
  const section = (title: string, items: NavLink[]) => {
    if (items.length) l.push({ type: 'subheader', title }, ...items)
  }

  l.push({
    title: 'Home',
    to: '/',
    prependIcon: 'mdi-home-outline',
  })

  if (live.value.name) {
    l.push({
      title: live.value.live ? 'LIVE NOW' : live.value.name,
      to: '/live',
      subtitle: live.value.live ? live.value.name : live.value.starts,
      prependIcon: 'mdi-radiobox-marked',
      color: 'red',
      disabled: !live.value.live,
      active: live.value.live,
    })
  }

  if (openFeature.getBool('box_office')) {
    l.push({
      title: 'Box Office',
      to: '/box-office',
      color: 'primary',
      prependIcon: 'mdi-ticket',
    })
  }

  if (openFeature.getBool('online_screening')) {
    l.push({
      title: 'Online Screening',
      to: '/online-screening',
      prependIcon: 'mdi-popcorn',
    })
  }

  const challenge: NavLink[] = []

  // Only offered during the challenge, when it's the first thing teams need.
  if (openFeature.getString('inspiration_package') != '') {
    challenge.push({
      title: 'Inspiration Package',
      to: '/film-challenge/inspiration',
      color: 'success',
      prependIcon: 'mdi-lightbulb-on',
    })
  }

  if (openFeature.getBool('festival_manager')) {
    challenge.push({
      title: 'Get Started',
      to: '/film-challenge',
      color: 'info',
      prependIcon: 'mdi-play-circle-outline',
    })

    if (openFeature.getBool('matchup_open')) {
      challenge.push({
        title: 'The Matchup',
        subtitle: 'Find a team to join',
        to: '/film-challenge/the-matchup',
        color: 'secondary',
        prependIcon: 'mdi-heart-search',
      })
    }

    if (!loaded.value) {
      challenge.push({ type: 'skeleton' })
    }

    for (const studio of studios.value || []) {
      const t: NavLink[] = []

      t.push({
        title: 'Studio History',
        to: `/film-challenge/studios/${studio.id}`,
        color: 'info',
        prependIcon: 'mdi-history',
      })

      if (
        studio.owner_id == user.value?.id &&
        openFeature.getBool('registration_open')
      ) {
        t.push({
          title: 'Create a Team',
          to: `/film-challenge/studios/${studio.id}/teams/create`,
          color: 'info',
          prependIcon: 'mdi-account-multiple-plus',
        })
      }

      for (const team of studio.teams) {
        t.push({
          title: team.name,
          to: `/film-challenge/studios/${studio.id}/teams/${team.id}`,
          prependIcon: 'mdi-account-group',
        })
      }

      challenge.push({
        title: studio.name,
        value: `studio/${studio.id}`,
        prependIcon: 'mdi-movie-open-play-outline',
        color: 'info',
        children: t,
      })
    }
  }

  section('Film Challenge', challenge)

  const fivefest: NavLink[] = []

  if (openFeature.getBool('five_fest')) {
    fivefest.push({
      title: 'Submit Film',
      to: '/fivefest/films/create',
      color: 'warning',
      prependIcon: 'mdi-plus',
    })

    if (!loaded.value) {
      fivefest.push({ type: 'skeleton' })
    }

    festivalSubmissions.value?.forEach(submission => {
      fivefest.push({
        title: submission.film_title,
        to: `/fivefest/films/${submission.id}`,
        color: 'warning',
        prependIcon: 'mdi-movie-open-play-outline',
        appendIcon: submission.submitted_at ? 'mdi-check' : 'mdi-alert',
        appendIconColor: submission.submitted_at ? 'success' : 'warning',
        appendIconSize: 'small',
        appendIconTooltip: submission.submitted_at
          ? 'Submitted'
          : 'Not Submitted',
      })
    })
  }

  section('FiveFest', fivefest)

  // Icons match the Group Manager types these ballots are cast in.
  section(
    'Vote',
    [
      { flag: 'selection_committee', type: 'selection', to: '/selection' },
      { flag: 'international_judging', type: 'judge', to: '/judge' },
      { flag: 'prize_sponsor', type: 'prize', title: 'Prize Sponsor', to: '/prize-sponsor' },
      { flag: 'five_fest_academy', type: 'fivefest', to: '/fivefest/academy' },
    ]
      .filter(v => openFeature.getBool(v.flag))
      .map(v => {
        const meta = groupTypeMeta(v.type)
        return { title: v.title ?? meta.title, to: v.to, prependIcon: meta.icon }
      }),
  )

  l.push({ type: 'subheader', title: 'Explore' })

  l.push({
    title: 'Top 10',
    to: '/archive/2026/top-10',
    prependIcon: 'mdi-trophy',
  })

  l.push({
    title: 'Archive',
    to: '/archive',
    prependIcon: 'mdi-archive',
  })

  const superUser = roles.value.includes('Super User')
  const staff: NavLink[] = []

  // Ordered the way a festival runs: people sign up and form teams, teams
  // are interviewed as they submit, films are grouped and judged, winners
  // are announced, then media is archived.
  if (superUser) {
    staff.push(
      {
        title: 'Users',
        to: '/admin/users',
        prependIcon: 'mdi-account-multiple-outline',
      },
      {
        title: 'Registrations',
        to: '/admin/registrations',
        prependIcon: 'mdi-clipboard-text-outline',
      },
      {
        title: 'Teams',
        to: '/admin/teams',
        prependIcon: 'mdi-account-group-outline',
      },
      {
        title: 'Studio Links',
        to: '/admin/studio-links',
        prependIcon: 'mdi-link-variant',
      },
    )
  }

  if (
    roles.value.includes('Exit Interviewer') &&
    openFeature.getBool('exit_interviews_open')
  ) {
    staff.push({
      title: 'Exit Interviews',
      to: '/admin/exit-interviews',
      prependIcon: 'mdi-video-account',
    })
  }

  if (superUser) {
    staff.push(
      {
        title: 'Group Manager',
        to: '/admin/group-manager',
        prependIcon: 'mdi-folder-account-outline',
      },
      {
        title: 'Results',
        value: 'admin/results',
        badge: String(festivalYear.value),
        prependIcon: 'mdi-podium',
        children: [
          {
            title: 'Overview',
            to: resultsPath(festivalYear.value),
            prependIcon: 'mdi-view-dashboard-outline',
          },
          ...SECTIONS.map(s => ({
            title: s.title,
            to: resultsPath(festivalYear.value, s.value),
            prependIcon: s.icon,
          })),
        ],
      },
      {
        title: 'Awards',
        value: 'admin/awards',
        prependIcon: 'mdi-medal-outline',
        children: [
          {
            title: 'Award Winners',
            to: '/admin/award-winners',
            prependIcon: 'mdi-trophy-award',
          },
          {
            title: 'Audience Favorite',
            to: '/admin/audience-favorite',
            prependIcon: 'mdi-heart-outline',
          },
          {
            title: 'Ceremony Check-in',
            to: '/admin/awards-ceremony/checkin',
            prependIcon: 'mdi-ticket-confirmation-outline',
          },
        ],
      },
    )
  }

  if (superUser || roles.value.includes('MAM')) {
    staff.push({
      title: 'Media Asset Manager',
      to: '/admin/mam',
      prependIcon: 'mdi-multimedia',
    })
  }

  // Older pages replaced by Group Manager and Results, kept for reference.
  if (superUser) {
    staff.push({
      title: 'Legacy',
      value: 'admin/legacy',
      prependIcon: 'mdi-archive-clock-outline',
      muted: true,
      children: [
        {
          title: 'Film Group Films',
          to: '/admin/film-groups/films',
          prependIcon: 'mdi-filmstrip-box-multiple',
        },
        {
          title: 'Film Group Users',
          to: '/admin/film-groups/users',
          prependIcon: 'mdi-account-box-multiple-outline',
        },
        ...SECTIONS.map(s => ({
          title: s.title,
          to: `/admin/results/${LEGACY_RESULTS[s.value]}`,
          prependIcon: s.icon,
        })),
      ],
    })
  }

  section(superUser ? 'Admin' : 'Staff', staff)

  return markActive(l)
})

const socials = [
  { icon: 'mdi-earth', title: 'SATO48.com', href: 'https://sato48.com' },
  { icon: 'mdi-instagram', title: 'Instagram', href: 'https://www.instagram.com/sato48_filmchallenge/' },
  { icon: 'mdi-facebook', title: 'Facebook', href: 'https://www.facebook.com/SATOFilmChallenge' },
  { icon: 'mdi-youtube', title: 'YouTube', href: 'https://www.youtube.com/@SATO48FILM' },
  { icon: 'mdi-vimeo', title: 'Vimeo', href: 'https://vimeo.com/user39636028' },
  { icon: 'mdi-patreon', title: 'Patreon', href: 'https://www.patreon.com/sato48' },
  { icon: 'mdi-tshirt-v', title: 'Merch', href: 'https://sato48.threadless.com' },
]

// Expand the groups leading to the current page, without closing ones the
// user opened. Collapsing to the rail closes everything; opening a group from
// the rail expands the drawer again.
const opened = ref<string[]>([])
const activeGroups = (items: NavLink[]): string[] =>
  items.flatMap(item => {
    if (!item.children) return []
    const inner = activeGroups(item.children)
    const here = inner.length || item.children.some(child => child.active)
    return here && item.value ? [item.value, ...inner] : inner
  })
watch(
  [() => route.path, loaded],
  () => {
    if (rail.value) return
    opened.value = [...new Set([...opened.value, ...activeGroups(links.value)])]
  },
  { immediate: true },
)
watch(rail, isRail => {
  if (isRail) opened.value = []
})
watch(opened, groups => {
  if (rail.value && groups.length) collapsed.value = false
})

const logo = 'https://img-cc.sato48.com/sato48/rs:fit:480/plain/s3://sato48-assets/sato48-logo-2026.svg'
</script>

<template>
  <v-navigation-drawer
    v-model="open"
    class="nav"
    mobile-breakpoint="md"
    :rail="rail"
    :width="268"
  >
    <template #prepend>
      <div class="nav-header" :class="{ rail }">
        <template v-if="rail">
          <v-tooltip text="Expand sidebar  [" location="end">
            <template #activator="{ props: tooltipProps }">
              <button
                v-bind="tooltipProps"
                class="rail-toggle"
                aria-label="Expand sidebar"
                @click="toggleRail"
              >
                <img src="/ios/64.png" alt="" width="32" height="32" />
                <v-icon icon="mdi-dock-left" />
              </button>
            </template>
          </v-tooltip>
        </template>
        <template v-else>
          <NuxtLink to="/" class="logo" aria-label="SATO48 Festival Manager home">
            <img :src="logo" alt="SATO48" />
          </NuxtLink>
          <v-spacer />
          <v-btn
            v-if="mobile"
            icon="mdi-close"
            variant="text"
            size="small"
            aria-label="Close menu"
            @click="open = false"
          />
          <v-tooltip v-else text="Collapse sidebar  [" location="end">
            <template #activator="{ props: tooltipProps }">
              <v-btn
                v-bind="tooltipProps"
                icon="mdi-dock-left"
                variant="text"
                size="small"
                class="text-medium-emphasis"
                aria-label="Collapse sidebar"
                @click="toggleRail"
              />
            </template>
          </v-tooltip>
        </template>
      </div>
      <div class="ds-split nav-split" />

      <v-list
        v-if="openFeature.getNumber('countdown_timer') > 0"
        density="compact"
        :lines="false"
        nav
        slim
      >
        <CountdownTimer />
      </v-list>
    </template>

    <v-list
      v-model:opened="opened"
      color="primary"
      density="compact"
      :lines="false"
      nav
      slim
    >
      <NavItem v-for="(item, i) in links" :key="i" :item="item" :rail="rail" />
    </v-list>

    <template #append>
      <div v-if="!rail" class="socials">
        <v-tooltip v-for="link in socials" :key="link.icon" :text="link.title" location="top">
          <template #activator="{ props: tooltipProps }">
            <v-btn
              v-bind="tooltipProps"
              :icon="link.icon"
              :href="link.href"
              :aria-label="link.title"
              target="_blank"
              variant="text"
              size="x-small"
              density="comfortable"
            />
          </template>
        </v-tooltip>
      </div>

      <NavVersion :rail="rail" />

      <div v-if="impersonating" class="px-2 pb-1">
        <v-tooltip :disabled="!rail" text="Stop masquerading" location="end">
          <template #activator="{ props: tooltipProps }">
            <v-btn
              v-bind="tooltipProps"
              :block="!rail"
              :icon="rail ? 'mdi-incognito-off' : undefined"
              :prepend-icon="rail ? undefined : 'mdi-incognito-off'"
              :size="rail ? 'small' : 'default'"
              :class="{ 'ml-1': rail }"
              color="error"
              variant="tonal"
              @click="impersonate()"
            >
              <template v-if="!rail">Stop masquerading</template>
            </v-btn>
          </template>
        </v-tooltip>
      </div>

      <v-divider />
      <NavUser :rail="rail" />
    </template>
  </v-navigation-drawer>
</template>

<style scoped>
.nav-header {
  display: flex;
  align-items: center;
  height: 64px;
  padding: 0 8px 0 16px;
}

.nav-split {
  height: 2px;
  border-radius: 0;
}

.nav-header.rail {
  justify-content: center;
  padding: 0;
}

.logo {
  display: flex;
  align-items: center;
}

.logo img {
  height: 40px;
  width: auto;
  display: block;
}

/* The app icon doubles as the expand control: hovering swaps it for the dock icon. */
.rail-toggle {
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  transition: background-color 120ms;
}

.rail-toggle > * {
  grid-area: 1 / 1;
  transition: opacity 120ms;
}

.rail-toggle .v-icon {
  opacity: 0;
  transform: scaleX(-1);
}

.rail-toggle:hover,
.rail-toggle:focus-visible {
  background: rgba(var(--v-theme-on-surface), 0.08);
}

.rail-toggle:hover img,
.rail-toggle:focus-visible img {
  opacity: 0;
}

.rail-toggle:hover .v-icon,
.rail-toggle:focus-visible .v-icon {
  opacity: 1;
}

.socials {
  display: flex;
  justify-content: space-between;
  padding: 4px 12px 8px;
  opacity: 0.6;
  transition: opacity 120ms;
}

.socials:hover,
.socials:focus-within {
  opacity: 1;
}
</style>
